import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, FileCheck2, BellRing, Truck, Lock, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: ShieldCheck, title: "Automatic RAG compliance", text: "Green, Amber and Red statuses update the moment documents change." },
  { icon: FileCheck2, title: "13 document categories", text: "Insurances, licences, registrations and safety declarations in one place." },
  { icon: BellRing, title: "Expiry alerts that never sleep", text: "Notifications at 30, 14 and 3 days before any document lapses." },
];

const fadeUp = (delay) => ({
  animation: `fade-up 0.6s cubic-bezier(0.22, 1, 0.36, 1) both`,
  animationDelay: `${delay}ms`,
});

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ business_name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const go = (user) => navigate(user.role === "admin" ? "/admin" : "/dashboard");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      go(user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (regForm.password !== regForm.confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const user = await register({
        business_name: regForm.business_name,
        email: regForm.email,
        password: regForm.password,
      });
      go(user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy" data-testid="auth-page">
      <video
        className="absolute inset-0 h-full w-full object-cover"
        src="/hero-video.mp4"
        poster="/hero-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy to-transparent" />

      <header className="relative z-10 flex items-center justify-between px-5 py-5 sm:px-10" style={fadeUp(0)}>
        <div className="flex items-center gap-3">
          <img src="/tagxpress-logo.png" alt="TAG Xpress logo" data-testid="auth-logo"
            className="h-11 w-auto rounded-md bg-white px-2.5 py-1.5 shadow-lg" />
          <div className="hidden text-[11px] uppercase tracking-[0.22em] leading-relaxed text-slate-300 sm:block">
            Carrier Compliance<br />Portal
          </div>
        </div>
        <div className="hidden items-center gap-2 text-xs text-slate-300 md:flex">
          <Lock className="h-3.5 w-3.5 text-brand" />
          Secure subcontractor access
        </div>
      </header>

      <main className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-16 pt-8 sm:px-10 lg:grid-cols-2 lg:gap-8 lg:pt-16">
        <div className="max-w-xl">
          <div style={fadeUp(100)} className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand backdrop-blur-sm">
            <Truck className="h-3.5 w-3.5" /> Local · Regional · Interstate
          </div>
          <h1 style={fadeUp(200)} className="font-heading text-4xl font-extrabold leading-[1.08] text-white sm:text-5xl lg:text-6xl">
            Compliance that keeps Australia&apos;s freight{" "}
            <span className="relative inline-block text-brand">
              moving.
              <span className="absolute -bottom-1 left-0 h-1 w-full rounded-full bg-brand/60" />
            </span>
          </h1>
          <p style={fadeUp(300)} className="mt-6 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg">
            One secure portal for TAG Xpress subcontractors and carriers to manage company details,
            insurance, licences and safety documentation.
          </p>

          <div className="mt-10 space-y-5">
            {FEATURES.map(({ icon: Icon, title, text }, i) => (
              <div key={title} style={fadeUp(400 + i * 120)} className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/5 backdrop-blur-sm">
                  <Icon className="h-5 w-5 text-brand" />
                </div>
                <div>
                  <div className="font-heading text-sm font-bold text-white">{title}</div>
                  <div className="mt-0.5 text-sm text-slate-400">{text}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={fadeUp(800)} className="mt-10 flex flex-wrap items-center gap-2.5">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Live status tracking:</span>
            {[
              { label: "Green — compliant", cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" },
              { label: "Amber — expiring", cls: "border-amber-400/30 bg-amber-400/10 text-amber-300" },
              { label: "Red — action needed", cls: "border-red-400/30 bg-red-400/10 text-red-300" },
            ].map((p) => (
              <span key={p.label} className={`rounded-full border px-3 py-1 text-xs font-semibold backdrop-blur-sm ${p.cls}`}>
                {p.label}
              </span>
            ))}
          </div>
        </div>

        <div style={fadeUp(350)} className="w-full max-w-md justify-self-center lg:justify-self-end">
          <div className="rounded-xl border border-white/20 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-8">
            <h2 className="font-heading text-2xl font-extrabold text-navy">Welcome</h2>
            <p className="mt-1 text-sm text-muted-foreground">Sign in or register your subcontractor business.</p>

            <Tabs defaultValue="login" className="mt-6" onValueChange={() => setError("")}>
              <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="mt-4 space-y-4" data-testid="login-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email">Email</Label>
                    <Input id="login-email" data-testid="login-email-input" type="email" required autoComplete="email"
                      value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      placeholder="you@company.com.au" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-password">Password</Label>
                    <Input id="login-password" data-testid="login-password-input" type="password" required autoComplete="current-password"
                      value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      placeholder="••••••••" />
                  </div>
                  {error && <div data-testid="auth-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                  <Button data-testid="login-submit-button" type="submit" disabled={loading}
                    className="group w-full bg-brand font-semibold text-white transition-transform duration-150 hover:-translate-y-px hover:bg-brand-dark">
                    {loading ? "Signing in…" : "Sign In"}
                    {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="mt-4 space-y-4" data-testid="register-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-business">Business name</Label>
                    <Input id="reg-business" data-testid="register-business-input" required
                      value={regForm.business_name} onChange={(e) => setRegForm({ ...regForm, business_name: e.target.value })}
                      placeholder="e.g. Smith Haulage Pty Ltd" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input id="reg-email" data-testid="register-email-input" type="email" required autoComplete="email"
                      value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                      placeholder="you@company.com.au" />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="reg-password">Password</Label>
                      <Input id="reg-password" data-testid="register-password-input" type="password" required minLength={8} autoComplete="new-password"
                        value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                        placeholder="Min. 8 characters" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="reg-confirm">Confirm password</Label>
                      <Input id="reg-confirm" data-testid="register-confirm-input" type="password" required autoComplete="new-password"
                        value={regForm.confirm} onChange={(e) => setRegForm({ ...regForm, confirm: e.target.value })}
                        placeholder="Repeat password" />
                    </div>
                  </div>
                  {error && <div data-testid="auth-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                  <Button data-testid="register-submit-button" type="submit" disabled={loading}
                    className="group w-full bg-brand font-semibold text-white transition-transform duration-150 hover:-translate-y-px hover:bg-brand-dark">
                    {loading ? "Creating account…" : "Create Account"}
                    {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
          <p className="mt-4 text-center text-xs text-slate-400">
            Protected by encrypted sessions & role-based access. Need help? Contact TAG Xpress.
          </p>
        </div>
      </main>
    </div>
  );
}
